import * as React from 'react';
import type { WholesaleProduct, WholesaleTier } from '../../types/wholesale-pricing';

interface WholesalePricingSectionProps {
  volume: number;
  onVolumeChange: (v: number) => void;
  tiers: WholesaleTier[];
  activeTier: WholesaleTier;
  products: WholesaleProduct[];
  isAuthenticated: boolean;
  downloadUrl: string;
  onRequestPriceList: () => void;
}

function formatCurrency(value: number): string {
  if (value >= 1_000_000) {
    const m = value / 1_000_000;
    return `$${m % 1 === 0 ? m.toFixed(0) : m.toFixed(1)}M`;
  }
  return `$${(value / 1000).toFixed(0)}K`;
}

function formatPrice(price: number): string {
  return '$' + price.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function WholesalePricingSection({
  volume,
  onVolumeChange,
  tiers,
  activeTier,
  products,
  isAuthenticated,
  downloadUrl,
  onRequestPriceList,
}: WholesalePricingSectionProps) {

  return (
    <section className="wholesale-pricing-section">
      <style>{`
        .wholesale-pricing-section {
          background: #fff;
        }
        .wholesale-pricing-container {
          max-width: 1400px;
          margin: 0 auto;
          display: grid;
          grid-template-columns: 320px 1fr;
          gap: 32px;
          padding: 32px 24px 64px;
        }
        
        /* Sidebar with slider - sticky */
        .wholesale-sidebar {
          position: sticky;
          top: 100px;
          height: fit-content;
          background: #f8fafc;
          border-radius: 16px;
          padding: 28px 24px;
          border: 1px solid #e2e8f0;
        }
        .wholesale-sidebar h2 {
          font-size: 18px;
          font-weight: 700;
          color: #1a1a1a;
          margin: 0 0 6px;
        }
        .wholesale-sidebar-subtitle {
          font-size: 13px;
          color: #64748b;
          margin: 0 0 24px;
        }
        .wholesale-volume-display {
          text-align: center;
          margin: 0 0 16px;
          padding: 16px;
          background: #fff;
          border-radius: 10px;
          border: 1px solid #e2e8f0;
        }
        .wholesale-volume-label {
          font-size: 12px;
          color: #64748b;
          margin: 0 0 4px;
        }
        .wholesale-volume-value {
          font-size: 24px;
          font-weight: 800;
          color: #1e293b;
        }
        .wholesale-range-wrap {
          padding: 0 4px;
          margin: 0 0 8px;
        }
        .wholesale-range {
          width: 100%;
          height: 6px;
          -webkit-appearance: none;
          appearance: none;
          background: #e2e8f0;
          border-radius: 3px;
          outline: none;
          cursor: pointer;
        }
        .wholesale-range::-webkit-slider-thumb {
          -webkit-appearance: none;
          width: 22px;
          height: 22px;
          border-radius: 50%;
          background: #1EAA25;
          border: 2px solid #fff;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
          cursor: pointer;
        }
        .wholesale-range::-moz-range-thumb {
          width: 22px;
          height: 22px;
          border-radius: 50%;
          background: #1EAA25;
          border: 2px solid #fff;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
          cursor: pointer;
        }
        .wholesale-range-labels {
          display: flex;
          justify-content: space-between;
          font-size: 11px;
          color: #94a3b8;
          margin: 0 0 20px;
        }
        .wholesale-tier-info {
          background: #fff;
          border-radius: 10px;
          padding: 16px;
          border: 2px solid #1EAA25;
          text-align: center;
          margin: 0 0 20px;
        }
        .wholesale-tier-name {
          font-size: 14px;
          font-weight: 700;
          color: #1e293b;
          margin: 0 0 4px;
        }
        .wholesale-tier-discount {
          font-size: 32px;
          font-weight: 800;
          color: #1EAA25;
          margin: 0 0 4px;
        }
        .wholesale-tier-volume {
          font-size: 12px;
          color: #64748b;
        }
        .wholesale-download-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          width: 100%;
          padding: 12px 16px;
          background: #1e293b;
          color: #fff;
          font-size: 14px;
          font-weight: 600;
          border-radius: 8px;
          text-decoration: none;
          transition: background 0.2s;
          border: none;
          cursor: pointer;
        }
        .wholesale-download-btn:hover {
          background: #334155;
          color: #fff;
          text-decoration: none;
        }
        
        /* Products grid */
        .wholesale-products-area {
          min-width: 0;
        }
        .wholesale-products-area h3 {
          font-size: 20px;
          font-weight: 700;
          color: #1a1a1a;
          margin: 0 0 6px;
        }
        .wholesale-products-subtitle {
          font-size: 14px;
          color: #64748b;
          margin: 0 0 24px;
        }
        .wholesale-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 16px;
        }
        .wholesale-card {
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          overflow: hidden;
          background: #fff;
          transition: box-shadow 0.2s;
          display: flex;
          flex-direction: column;
        }
        .wholesale-card:hover {
          box-shadow: 0 4px 12px rgba(0,0,0,0.08);
        }
        .wholesale-card-img {
          width: 100%;
          aspect-ratio: 4/3;
          object-fit: cover;
          background: #f1f5f9;
        }
        .wholesale-card-body {
          padding: 10px 12px 14px;
          flex: 1;
          display: flex;
          flex-direction: column;
        }
        .wholesale-card-category {
          font-size: 10px;
          font-weight: 600;
          color: #94a3b8;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin: 0 0 3px;
        }
        .wholesale-card-title {
          font-size: 13px;
          font-weight: 600;
          color: #1e293b;
          margin: 0 0 3px;
          line-height: 1.3;
        }
        .wholesale-card-format {
          font-size: 12px;
          color: #64748b;
          margin: 0 0 8px;
        }
        .wholesale-card-prices {
          display: flex;
          align-items: baseline;
          gap: 6px;
          margin-top: auto;
        }
        .wholesale-card-price {
          font-size: 16px;
          font-weight: 800;
          color: #1EAA25;
        }
        .wholesale-card-base-price {
          font-size: 12px;
          color: #94a3b8;
          text-decoration: line-through;
        }
        
        @media (max-width: 1200px) {
          .wholesale-pricing-container {
            grid-template-columns: 280px 1fr;
            gap: 24px;
          }
          .wholesale-grid {
            grid-template-columns: repeat(3, 1fr);
          }
        }
        @media (max-width: 900px) {
          .wholesale-pricing-container {
            grid-template-columns: 1fr;
          }
          .wholesale-sidebar {
            position: relative;
            top: 0;
            order: -1;
          }
          .wholesale-grid {
            grid-template-columns: repeat(4, 1fr);
          }
        }
        @media (max-width: 768px) {
          .wholesale-grid {
            grid-template-columns: repeat(3, 1fr);
            gap: 12px;
          }
        }
        @media (max-width: 640px) {
          .wholesale-pricing-container {
            padding: 20px 16px 48px;
          }
          .wholesale-sidebar {
            padding: 20px 16px;
          }
          .wholesale-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 10px;
          }
          .wholesale-card-body {
            padding: 8px 10px 12px;
          }
          .wholesale-card-title {
            font-size: 12px;
          }
          .wholesale-card-price {
            font-size: 14px;
          }
        }
      `}</style>
      
      <div className="wholesale-pricing-container">
        {/* Sticky Sidebar with Slider */}
        <aside className="wholesale-sidebar">
          <h2>Elegí tu volumen</h2>
          <p className="wholesale-sidebar-subtitle">Mové el slider para ver precios</p>
          
          <div className="wholesale-volume-display">
            <p className="wholesale-volume-label">Volumen mensual</p>
            <p className="wholesale-volume-value">{formatCurrency(volume)}/mes</p>
          </div>
          
          <div className="wholesale-range-wrap">
            <input
              type="range"
              className="wholesale-range"
              min={200000}
              max={5000000}
              step={50000}
              value={volume}
              onChange={e => onVolumeChange(Number(e.target.value))}
            />
          </div>
          <div className="wholesale-range-labels">
            <span>$200K</span>
            <span>$5M</span>
          </div>
          
          <div className="wholesale-tier-info">
            <p className="wholesale-tier-name">{activeTier.name}</p>
            <p className="wholesale-tier-discount">{Math.abs(activeTier.percentage)}% OFF</p>
            <p className="wholesale-tier-volume">
              {activeTier.max_volume
                ? `${formatCurrency(activeTier.min_volume)} – ${formatCurrency(activeTier.max_volume)}`
                : `Desde ${formatCurrency(activeTier.min_volume)}`}
            </p>
          </div>
          
          {isAuthenticated ? (
            <a href={downloadUrl} className="wholesale-download-btn">
              <i className="fa fa-file-excel" /> Descargar lista
            </a>
          ) : (
            <button onClick={onRequestPriceList} className="wholesale-download-btn">
              <i className="fa fa-file-excel" /> Descargar lista
            </button>
          )}
        </aside>
        
        {/* Products Grid */}
        <div className="wholesale-products-area">
          <h3>Los más pedidos por empresas</h3>
          <p className="wholesale-products-subtitle">Top productos comprados por negocios como el tuyo</p>
          
          <div className="wholesale-grid">
            {products.map(product => {
              const tierPrice = product.tier_prices[activeTier.name] ?? product.base_price;
              const showDiscount = tierPrice < product.base_price;

              return (
                <div key={product.id} className="wholesale-card">
                  <picture>
                    <source srcSet={product.webp_image_url} type="image/webp" />
                    <img
                      className="wholesale-card-img"
                      src={product.image_url}
                      alt={product.title}
                      loading="lazy"
                    />
                  </picture>
                  <div className="wholesale-card-body">
                    <p className="wholesale-card-category">{product.category_name}</p>
                    <p className="wholesale-card-title">{product.title}</p>
                    <p className="wholesale-card-format">{product.format_name} · {product.unit_name}</p>
                    <div className="wholesale-card-prices">
                      <span className="wholesale-card-price">{formatPrice(tierPrice)}</span>
                      {showDiscount && (
                        <span className="wholesale-card-base-price">{formatPrice(product.base_price)}</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
